import React, { useRef, useState, useEffect } from "react";
import "./CustomDropdown.css";
import ArrowDown from "../../assets/icons/Group 33921.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import FallbackImg from "../../components/FallbackImg";

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "",
  className = "",
  renderOption, // optional custom render function
  disabled = false,
  isMulti = false,
  popupRef,
  hideSearchbar = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const [openUpward, setOpenUpward] = useState(false);
  const menuRef = useRef(null);

  // When opening, check if there's enough space below; if not, open upward
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuHeight = 220; // Estimate or measure your menu height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If not enough space below but enough above, open upward
      if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popupRef && popupRef.current && popupRef.current.contains(e.target))
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popupRef]);

  const selected = options.find((opt) => opt.value === value);

  const isOptionSelected = (opt) => {
    if (isMulti && Array.isArray(value)) {
      return value.some(
        (v) => JSON.stringify(v.value) === JSON.stringify(opt.value)
      );
    }

    // For single select, compare value objects by content
    // return JSON.stringify(opt.value) === JSON.stringify(value.value);

    // For single select, compare directly if value is a string
    return value === opt.value;
  };

  // Disable browser scrolling when the dropdown is open
  useEffect(() => {
    if (open && !disabled) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, disabled]);

  return (
    <>
      {open && !disabled && (
        <div
          className="custom-dropdown-overlay"
          onClick={() => setOpen(false)}
        />
      )}
      <div className={`custom-dropdown-container ${className}`} ref={ref}>
        <div
          className={`custom-dropdown-selected ${className} ${
            disabled ? "disabled" : ""
          }`}
          onClick={() => {
            if (!disabled) setOpen((o) => !o);
          }}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
        >
          <div>
            {isMulti
              ? Array.isArray(value) && value.length > 0
                ? value.map((v, idx) => (
                    <span
                      key={JSON.stringify(v.value)}
                      className="custom-dropdown-multi-tag"
                    >
                      {renderOption ? renderOption(v) : v.label}
                      {value.length > 1 && idx < value.length - 1 ? ", " : ""}
                    </span>
                  ))
                : placeholder
              : selected
              ? renderOption
                ? renderOption(selected)
                : selected.label
              : placeholder}
          </div>
          <div className="custom-dropdown-arrowdown">
            <FallbackImg
              src={ArrowDown}
              alt="arrow"
              width="15"
              height="15"
              className={open ? "rotate" : ""}
              style={disabled ? { opacity: 0.5 } : {}}
            />
          </div>
        </div>
        {open && !disabled && (
          <div
            ref={menuRef}
            className={`custom-dropdown-menu ${className} ${
              openUpward ? "open-upward" : ""
            }`}
          >
            {!hideSearchbar && (
              <div className="searchbar-container-customdropdown">
                <div className="searchbar-customdropdown">
                  <FontAwesomeIcon
                    className="search-icon"
                    icon={faMagnifyingGlass}
                    style={{ color: "#aeadac" }}
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    autoFocus
                    style={{
                      margin: "0",
                      padding: "10px 30px",
                      fontSize: "12px",
                      width: "100%",
                      border: "none",
                      outline: "none",
                      backgroundColor: "#ededed",
                      borderRadius: "20px",
                    }}
                  />
                </div>
              </div>
            )}
            {options
              .filter((opt) => {
                if (!search) return true;
                // If label is a string
                if (typeof opt.label === "string") {
                  return opt.label.toLowerCase().includes(search.toLowerCase());
                }
                // If label is a React element (e.g. with logo), try to match by value
                if (typeof opt.value === "string") {
                  return opt.value.toLowerCase().includes(search.toLowerCase());
                }
                return false;
              })
              .map((opt) => {
                // console.log(
                //   "Option:",
                //   opt,
                //   "Selected:",
                //   isOptionSelected(opt),
                //   "Current value:",
                //   value
                // );
                return (
                  <div
                    key={opt.value}
                    className={`custom-dropdown-item${
                      isOptionSelected(opt) ? " selected" : ""
                    }`}
                    style={{ padding: "8px 18px", minHeight: 38 }}
                    onClick={() => {
                      if (disabled) return;
                      if (isMulti) {
                        let newValue = Array.isArray(value) ? [...value] : [];
                        if (isOptionSelected(opt)) {
                          newValue = newValue.filter(
                            (v) => v.value !== opt.value
                          );
                        } else {
                          newValue.push(opt);
                        }
                        onChange(newValue, opt);
                      } else {
                        onChange(opt.value, opt);
                        setOpen(false);
                      }
                    }}
                  >
                    <span>{renderOption ? renderOption(opt) : opt.label}</span>
                    {isOptionSelected(opt) && (
                      <div className="custom-dropdown-tick">
                        <svg viewBox="0 0 20 20" width="16" height="16">
                          <polyline
                            points="4,10 8,14 16,6"
                            fill="none"
                            // stroke="#4CAF50"
                            stroke="#000000"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </>
  );
};

export default CustomDropdown;
